import {
  DateInput,
  Dropdown,
  Icon,
  Search,
  SearchEngineType,
} from "@hoylu/client-common";
import DefaultDropdownStyles from "@hoylu/client-common/dist/assets/css.modules/default.dropdown.module.css";
import { DateTime } from "luxon";
import React, { useContext } from "react";
import { useI18n } from "../../utils/hooks/use.i18n";
import { useSingleSelectDropdownTranslation } from "../../utils/hooks/useSingleSelectDropdownTranslation";
import Styles from "./Analytics.module.css";
import { FilterAction, FilterContext } from "./FilterProvider";
import { ReportFactory, type ReportType } from "./ReportFactory";
import { TaskList } from "./TaskListView/TaskList";

export type AnalyticsProps = {
  type: ReportType;
};

const FILTER_ORDER_MAP: { [key: string]: number } = {
  team: 0,
  assignee: 1,
  status: 2,
  label: 3,
  workspace: 4,
};

const FilterSorter = (a: [string, any], b: [string, any]) => {
  return (
    (FILTER_ORDER_MAP[a[0]] ?? Infinity) - (FILTER_ORDER_MAP[b[0]] ?? Infinity)
  );
};

export function Analytics({ type }: AnalyticsProps) {
  const filterContext = useContext(FilterContext);
  const t = useI18n("ANALYTICS.");
  const dropdownTranslation = useSingleSelectDropdownTranslation();

  return (
    <>
      <div className={Styles.filters}>
        <DateInput
          label={t("DATE_FROM")}
          onChange={(v) =>
            filterContext.filterDispatch(FilterAction.setDateRange("from", v))
          }
          range={filterContext.dateRange}
          style={{ background: "inherit", borderBottomStyle: "none" }}
          value={
            filterContext.filterState.from
              ? DateTime.fromISO(filterContext.filterState.from)
              : undefined
          }
        />
        <DateInput
          label={t("DATE_TO")}
          onChange={(v) =>
            filterContext.filterDispatch(FilterAction.setDateRange("to", v))
          }
          range={filterContext.dateRange}
          style={{ background: "inherit", borderBottomStyle: "none" }}
          value={
            filterContext.filterState.to
              ? DateTime.fromISO(filterContext.filterState.to)
              : undefined
          }
        />
        {Object.entries(filterContext.filterState.filters)
          .sort(FilterSorter)
          .map(([key, options]) => {
            return (
              <Dropdown
                allowReset={true}
                allowSelectAll={true}
                classes={[DefaultDropdownStyles.dropdown, Styles.dropdown]}
                key={key}
                localizationDict={dropdownTranslation}
                onChange={(value) =>
                  filterContext.filterDispatch(
                    FilterAction.setSelectedOptions(key, value),
                  )
                }
                options={options.availableOptions}
                searchEngineType={SearchEngineType.FUZZY}
                title={key}
                useMultiSelect={true}
                values={options.selectedOptions}
              />
            );
          })}
        <Search
          handleChange={(q) =>
            filterContext.filterDispatch(FilterAction.setQuery(q))
          }
          value={filterContext.filterState.query}
        />
        <Icon
          icon="hoylu-ui-icons-refresh"
          onClick={() => filterContext.filterDispatch(FilterAction.refresh())}
          title={t("REFRESH")}
        />
      </div>
      <div className={Styles.report}>
        <ReportFactory type={type} />
      </div>
      <TaskList />
    </>
  );
}
